import { Box3, ClippingGroup, Plane, Quaternion, Vector2, Vector3, Vector4 } from 'three/webgpu'
import gsap from 'gsap'
import { easing } from 'maath'
import ImportGltf from '../utils/ImportGltf'
import ClawRig from '../utils/ClawRig'
import ClawPrizes from '../utils/ClawPrizes'
import DomTracked from './DomTracked'
import ShadowCatcher from './ShadowCatcher'
import RoundedPanel from './RoundedPanel'
import staticPath from '../utils/staticPath'
import AgxPhysicalMaterial from '../materials/AgxPhysicalMaterial'

export default class Claw extends DomTracked {
	static defaults = {
		url: staticPath('models/raw/claw_raw_export.glb'),
		renderer: null,
		section: null,
		size: { start: 2.2, end: 1.1 },
		raise: { start: 0.15, end: 0 },
		rod: 3,
		clip: { spread: 0.1 },
		gradient: { top: 0x191716, bottom: 0x2e2c29 },
		expand: { distance: 1, warp: 0.4, waves: 1, ripple: 0.02, bounce: 0.25, falloff: 0.35 },
		panelDepth: 0.3,
		yaw: -Math.PI / 2,
		exposure: 1,
		follow: { amount: 1, limit: 0.3, smoothing: 0.2, tilt: 0.1 },
		grab: {
			open: 1,
			close: 1.5,
			hold: 0.4,
			closeAt: 0.2,
			riseAt: 0.9,
			openAt: 1.7,
			duration: 3,
			smoothing: 0.15,
		},
		physics: staticPath('physics.js'),
		shadow: { size: 8, reach: 3, opacity: 0.15, intensity: 2.5, position: { x: 1, y: -1.5, z: 3 } },
	}

	constructor(options = {}) {
		super(options)

		this.bounds = new Box3()
		this.centre = new Vector3()
		this.extent = new Vector3()
		this.eye = new Vector3()
		this.edge = new Vector3()
		this.across = new Vector3()
		this.shift = new Vector2()
		this.clip = new Plane()
		this.clipper = new ClippingGroup()
		this.clipper.clippingPlanes = [this.clip]
		this.add(this.clipper)
		this.inverse = new Quaternion()
		this.progress = 0
		this.from = new Vector4()
		this.to = new Vector4()
		this.pointerX = window.innerWidth / 2
		this.target = 0

		this._onPointerMove = event => {
			this.pointerX = event.clientX
		}

		this._onClick = () => {
			if (this.grabTime === null) this.grabTime = 0
		}

		this.grabTime = null
		this.drop = 0
		this.close = 0
		this.squeeze = 0

		const style = getComputedStyle(this.element)
		const { warp, waves, ripple, bounce, falloff } = this.options.expand

		this.panel = new RoundedPanel({
			...this.options.gradient,
			radius: parseFloat(style.borderRadius) || 0,
			warp,
			waves,
			ripple,
			bounce,
			falloff,
		})
		this.add(this.panel)

		window.addEventListener('pointermove', this._onPointerMove)
		this.options.section.addEventListener('click', this._onClick)
		this.#expand()
	}

	#measure() {
		const { innerHeight } = window
		const { clientWidth } = document.documentElement
		const { offsetLeft, offsetWidth, offsetHeight } = this.element

		this.from.set(offsetLeft, (innerHeight - offsetHeight) / 2, offsetWidth, offsetHeight)
		this.to.set(0, 0, clientWidth, innerHeight)

		return this.to
	}

	#lock() {
		const { offsetTop, offsetHeight } = this.element
		const top = this.options.section.getBoundingClientRect().top + window.scrollY

		return top + offsetTop + offsetHeight / 2 - window.innerHeight / 2
	}

	#expand() {
		const { distance } = this.options.expand
		const element = this.element
		const travel = () => window.innerHeight * distance

		this.timeline = gsap
			.timeline({
				scrollTrigger: {
					start: () => this.#lock(),
					end: () => `+=${travel()}`,
					scrub: true,
					invalidateOnRefresh: true,
					onUpdate: ({ progress }) => {
						this.progress = progress
					},
				},
			})
			.to(
				element,
				{
					x: () => this.#measure().x - this.from.x,
					y: () => travel() + this.#measure().y - this.from.y,
					scaleX: () => this.#measure().z / this.from.z,
					scaleY: () => this.#measure().w / this.from.w,
					transformOrigin: '0 0',
					ease: 'none',
				},
				0
			)
	}

	async init(onProgress) {
		this.importer = new ImportGltf(this.options.url, { renderer: this.options.renderer })

		const [model, { default: RAPIER }] = await Promise.all([
			this.importer.load(onProgress),
			import(this.options.physics),
		])

		this.model = model

		this.model.traverse(child => {
			for (const material of [child.material ?? []].flat()) {
				AgxPhysicalMaterial.adopt(material, { exposure: this.options.exposure })
			}
		})

		this.rig = new ClawRig(this.model)

		const { body } = this.rig

		body.rotation.order = 'ZYX'
		body.rotation.y += this.options.yaw

		this.prizes = new ClawPrizes(this.model, body, RAPIER)

		this.bounds.setFromObject(this.model)

		this.bounds.getCenter(this.centre)
		this.bounds.getSize(this.extent)
		this.rig.rod.extend(this.options.rod)
		this.rest = {
			x: this.rig.body.position.x,
			y: this.rig.body.position.y,
			z: this.rig.body.rotation.z,
			plunger: this.rig.plunger.position.y,
		}
		this.x = this.rest.x
		this.clipper.add(this.model)
		this.shadow = new ShadowCatcher(this.options.shadow)
		this.shadow.rotation.x = -Math.PI / 2
		this.clipper.add(this.shadow)

		return this
	}

	layout(rect) {
		this.expanded = this.progress > 0.99

		if (!this.model) return this.#backdrop(rect, 0)

		const size = this.#blend(this.options.size)
		const raise = this.#blend(this.options.raise)
		const scale = (rect.height * this.worldPerPixel * size) / this.extent.y

		this.model.scale.setScalar(scale)
		this.model.position.set(
			-this.centre.x * scale,
			(rect.height / 2 + rect.height * raise) * this.worldPerPixel - this.bounds.max.y * scale,
			-this.centre.z * scale
		)

		this.#backdrop(rect, (this.centre.z - this.bounds.min.z) * scale * this.options.panelDepth)
		this.#clip(rect)

		this.shadow.position.set(
			this.model.position.x,
			this.model.position.y + this.prizes.floor * scale,
			this.model.position.z
		)

		const plane = this.camera.position.z - this.position.z
		const depth = plane - this.model.position.z

		this.prizes.fit(((rect.width / 2) * this.worldPerPixel * depth) / plane / scale)
		const { amount, limit } = this.options.follow
		const reach = rect.width * limit
		const offset = Math.min(reach, Math.max(-reach, this.pointerX - rect.left - rect.width / 2))

		this.target = this.rest.x + (offset * this.worldPerPixel * amount) / scale
	}

	#blend({ start, end }) {
		return start + (end - start) * this.progress
	}

	#clip(rect) {
		const { spread } = this.options.clip
		const centre = this.rig.body.getWorldPosition(this.edge).project(this.camera)
		const across = (((centre.x + 1) / 2) * window.innerWidth - rect.left) / rect.width
		const u = Math.min(1 - spread, Math.max(spread, across))

		this.#through(this.clip, this.#rim(rect, u - spread, 1, this.edge), this.#rim(rect, u + spread, 1, this.across))
	}

	#through(plane, from, to) {
		const camera = this.camera.position

		plane.setFromNormalAndCoplanarPoint(from.sub(camera).cross(to.sub(camera)).normalize(), camera)
	}

	#rim(rect, u, v, target) {
		const shift = this.panel.material.offset(u, v, this.shift)
		const x = rect.left + u * rect.width + shift.x
		const y = rect.top + (1 - v) * rect.height + shift.y

		return target.set((x / window.innerWidth) * 2 - 1, -(y / window.innerHeight) * 2 + 1, 0.5).unproject(this.camera)
	}

	#backdrop(rect, back) {
		this.eye.copy(this.camera.position).sub(this.position).applyQuaternion(this.inverse.copy(this.quaternion).invert())

		const reach = 1 + back / this.eye.z

		this.panel.position.copy(this.eye).multiplyScalar(1 - reach)
		this.panel.scale.set(rect.width * this.worldPerPixel * reach, rect.height * this.worldPerPixel * reach, 1)
		this.panel.resize(rect)
		this.panel.morph(this.progress, this.from, this.to)
	}

	update(delta, elapsed) {
		super.update(delta, elapsed)

		if (!this.rig || !this.visible) return

		const { body, pivot } = this.rig
		const { smoothing, tilt } = this.options.follow
		const lag = this.target - this.x

		easing.damp(this, 'x', this.target, smoothing, delta)
		easing.damp(body.rotation, 'z', this.rest.z - lag * tilt, smoothing, delta)

		this.prizes.visible = this.expanded
		this.shadow.visible = this.expanded
		this.#grab(delta)
		this.#hang(body, pivot)
		this.rig.update(delta)
		this.prizes.update(delta, this.close > 0.5, this.grabTime !== null)
	}

	#hang(body, pivot) {
		const angle = body.rotation.z - this.rest.z

		body.position.x = this.x + pivot * Math.sin(angle)
		body.position.y += pivot * (1 - Math.cos(angle))
	}

	#grab(delta) {
		const { open, close, hold, closeAt, riseAt, openAt, duration, smoothing } = this.options.grab
		const time = this.grabTime ?? -1

		easing.damp(this, 'drop', time >= 0 && time < riseAt ? 1 : 0, smoothing, delta)
		easing.damp(this, 'close', time >= closeAt && time < openAt ? 1 : 0, smoothing, delta)

		this.rig.body.position.y = this.rest.y - this.drop * this.prizes.stop
		easing.damp(this, 'squeeze', this.prizes.holding ? hold : close, smoothing, delta)

		this.rig.plunger.position.y = this.rest.plunger - this.drop * (1 - this.close) * open + this.close * this.squeeze

		if (this.grabTime === null) return

		this.grabTime += delta
		if (this.grabTime > duration) this.grabTime = null
	}

	dispose() {
		super.dispose()
		window.removeEventListener('pointermove', this._onPointerMove)
		this.options.section.removeEventListener('click', this._onClick)
		this.timeline.revert()
		this.panel.dispose()
		this.importer?.dispose()
		this.importer = null
		this.model = null
		this.rig = null
		this.prizes?.dispose()
		this.shadow?.dispose()
		this.shadow = null
		this.prizes = null
	}
}
